import bellabotPro from "@/assets/blog/bellabot-pro-restaurant.png.asset.json";
import aiDashboard from "@/assets/blog/ai-employees-dashboard.png.asset.json";
import puduT600 from "@/assets/blog/pudu-t600-warehouse.png.asset.json";
import cc1ProLobby from "@/assets/blog/cc1-pro-lobby.png.asset.json";
import t600Fleet from "@/assets/blog/t600-fleet.jpeg.asset.json";
import warehouseNav from "@/assets/blog/warehouse-navigation.jpeg.asset.json";
import cooksCase from "@/assets/cooks-flea-market-case-study.jpeg.asset.json";
import cooksHero from "@/assets/cooks_hero_robotics.png.asset.json";

export type BlogSection = {
  heading: string;
  body: string[];
};

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  imageAlt: string;
  category: string;
  date: string;
  readTime: string;
  author: string;
  tags: string[];
  featured?: boolean;
  intro: string;
  sections: BlogSection[];
  pullQuote?: string;
  related: string[]; // slugs
};

export const BLOG_CATEGORIES = [
  "All",
  "AI Automation",
  "PUDU Robotics",
  "Cleaning Robots",
  "Service Robots",
  "Case Studies",
  "ROI Guides",
  "Hotels",
  "Restaurants",
  "Warehouses",
  "Healthcare",
  "Churches",
] as const;

export const blogPosts: BlogPost[] = [
  {
    slug: "is-your-business-ready-for-ai-robotics",
    title: "How to Know If Your Business Is Ready for AI & Robotics",
    excerpt:
      "A practical guide for business owners who want to reduce burnout, improve operations, and understand whether AI employees or robotics are the right next step.",
    image: cooksHero.url,
    imageAlt: "Business owner reviewing AI automation strategy",
    category: "ROI Guides",
    date: "2026-06-18",
    readTime: "9 min read",
    author: "Neil AI Team",
    tags: ["AI Automation", "Robotics", "Strategy", "Operations"],
    featured: true,
    intro:
      "Most owners don't need a futurist — they need a clear, honest answer to one question: is automation actually the right move for my business right now? This guide gives you a practical readiness check, free of hype.",
    sections: [
      {
        heading: "Signs your team is ready for AI support",
        body: [
          "Your front desk, dispatcher, or sales team is missing calls or follow-ups. Staff are working past close to catch up on admin tasks. Customer response times are slipping during peak hours.",
          "When these patterns show up week after week, the bottleneck usually isn't effort — it's bandwidth. AI employees and voice agents are designed to absorb repetitive, time-sensitive work so your people can focus on customers and revenue.",
        ],
      },
      {
        heading: "Signs your operations are ready for robotics",
        body: [
          "You're cleaning the same square footage every day with manual labor. Your team is moving heavy carts, trays, or pallets across long distances. You're paying overtime to cover repetitive runs.",
          "Service robots like BellaBot Pro, cleaning robots like the CC1 Pro, and transport robots like the PUDU T600 are most effective when the work is repeatable, predictable, and physically draining — exactly the work that wears teams out fastest.",
        ],
      },
      {
        heading: "Questions to answer before you invest",
        body: [
          "What specific tasks would you remove from your team this quarter? How many hours per week does that free up? What would your team do with that time — serve more guests, close more deals, deliver better care?",
          "If you can answer those three questions clearly, you're ready to talk to a specialist. If you can't yet, a free automation review is the right next step.",
        ],
      },
    ],
    pullQuote:
      "We don't replace your team. We remove the work that's wearing them down.",
    related: [
      "complete-roi-guide-ai-robotics",
      "pudu-cleaning-robots-reduce-burnout",
      "voice-ai-business-development-rep",
    ],
  },
  {
    slug: "complete-roi-guide-ai-robotics",
    title: "The Complete ROI Guide for AI & Robotics Investments",
    excerpt:
      "How to think about payback, labor relief, and total cost of ownership when evaluating AI employees, cleaning robots, and service robots for your business.",
    image: aiDashboard.url,
    imageAlt: "AI analytics dashboard showing automation ROI",
    category: "ROI Guides",
    date: "2026-06-15",
    readTime: "10 min read",
    author: "Neil AI Team",
    tags: ["ROI", "AI Automation", "Robotics", "Finance"],
    intro:
      "ROI on AI and robotics isn't only about raw dollar savings. It includes labor relief, retention, customer experience, and capacity. Here's a framework you can apply to any quote you receive.",
    sections: [
      {
        heading: "The four buckets of ROI",
        body: [
          "Direct labor offset: hours per week the system covers that would otherwise be paid to staff or overtime.",
          "Capacity gain: new revenue or throughput your team can produce because repetitive work is handled.",
          "Retention impact: reduced turnover when burnout-heavy tasks are removed from your team.",
          "Customer experience: faster response times, more consistent service, fewer missed touchpoints.",
        ],
      },
      {
        heading: "How to estimate payback honestly",
        body: [
          "Start with conservative numbers. Use your real hourly fully-loaded labor cost — wages plus payroll taxes, benefits, and turnover cost. Compare it to the monthly cost of the system, including financing if you use it.",
          "Most business owners we work with see meaningful payback inside the first year on cleaning and service robots, and even faster on AI voice agents that recover missed calls.",
        ],
      },
      {
        heading: "What to ask before you sign",
        body: [
          "What's included in support and training? What happens if a robot needs service? Who handles software updates? Is financing available and what are the real monthly numbers?",
          "A good partner answers all of these in plain language. If you ever feel pressured or confused, slow down and ask for a written breakdown.",
        ],
      },
    ],
    pullQuote:
      "If the math only works with aggressive assumptions, the deal isn't ready yet.",
    related: [
      "is-your-business-ready-for-ai-robotics",
      "what-to-ask-before-buying-cleaning-robot",
      "voice-ai-business-development-rep",
    ],
  },
  {
    slug: "pudu-cleaning-robots-reduce-burnout",
    title: "How PUDU Cleaning Robots Help Reduce Staff Burnout",
    excerpt:
      "Cleaning teams burn out fast on repetitive floor work. Here's how the CC1 Pro and SH1 Scrubber take the strain off your team without removing jobs.",
    image: cc1ProLobby.url,
    imageAlt: "PUDU CC1 Pro cleaning robot in a luxury lobby",
    category: "Cleaning Robots",
    date: "2026-06-12",
    readTime: "7 min read",
    author: "Neil AI Team",
    tags: ["PUDU Robotics", "Cleaning Robots", "CC1 Pro", "SH1", "Burnout"],
    intro:
      "Cleaning crews don't quit because they don't like the work. They quit because the repetitive, physically demanding parts of the job wear them down. Robots fix exactly that.",
    sections: [
      {
        heading: "Where burnout actually comes from",
        body: [
          "Mopping the same lobby floor every night. Pushing heavy auto-scrubbers across thousands of square feet. Doing the same overnight loop in a hotel or facility for years.",
          "These tasks are repetitive, hard on the body, and offer no career growth. They're the first thing automation should absorb.",
        ],
      },
      {
        heading: "What the CC1 Pro and SH1 handle for you",
        body: [
          "Autonomous floor scrubbing and vacuuming on a programmed schedule, including overnight runs.",
          "Consistent coverage across complex spaces with safe navigation around guests, staff, and obstacles.",
          "Detailed cleaning logs your team and customers can review.",
        ],
      },
      {
        heading: "What your team gets to do instead",
        body: [
          "Detail work. Guest-facing tasks. Higher-skill cleaning that actually shows. The kind of work that retains employees and earns better pay over time.",
        ],
      },
    ],
    pullQuote:
      "Automation should remove the work that breaks people, not the people themselves.",
    related: [
      "cc1-pro-vs-traditional-cleaning",
      "when-hotels-should-consider-cleaning-robots",
      "cooks-flea-market-sh1-case-study",
    ],
  },
  {
    slug: "cc1-pro-vs-traditional-cleaning",
    title: "CC1 Pro vs Traditional Cleaning: What Business Owners Should Know",
    excerpt:
      "An honest side-by-side look at autonomous floor cleaning vs traditional manual cleaning — coverage, consistency, labor, and total cost.",
    image: cc1ProLobby.url,
    imageAlt: "CC1 Pro autonomous floor cleaning robot",
    category: "Cleaning Robots",
    date: "2026-06-09",
    readTime: "6 min read",
    author: "Neil AI Team",
    tags: ["CC1 Pro", "PUDU Robotics", "Cleaning Robots", "Comparison"],
    intro:
      "The CC1 Pro isn't a magic wand and it isn't a replacement for a real cleaning crew. Here's where it wins, where traditional cleaning still wins, and how to combine both for the best result.",
    sections: [
      {
        heading: "Where the CC1 Pro is stronger",
        body: [
          "Large, open, repeatable floor space — lobbies, hallways, retail aisles, big-box layouts.",
          "Consistent overnight schedules that your team would otherwise need to cover with overtime.",
          "Detailed reporting so you can prove cleaning happened.",
        ],
      },
      {
        heading: "Where humans are still essential",
        body: [
          "Restrooms, detail work, high-touch surfaces, anything requiring judgment, and recovery cleaning after an event.",
          "The right setup is robot + crew, not robot vs crew.",
        ],
      },
      {
        heading: "How to evaluate the switch",
        body: [
          "Add up the hours your crew currently spends on the exact tasks the CC1 Pro would take over. Compare to the monthly cost. Most facilities of meaningful size see the math work out within the first year.",
        ],
      },
    ],
    related: [
      "pudu-cleaning-robots-reduce-burnout",
      "what-to-ask-before-buying-cleaning-robot",
      "when-hotels-should-consider-cleaning-robots",
    ],
  },
  {
    slug: "when-hotels-should-consider-cleaning-robots",
    title: "When Hotels Should Consider Cleaning Robots",
    excerpt:
      "Not every property needs robotics. Here are the specific signs that tell you a hotel is ready to bring autonomous cleaning into the operation.",
    image: cc1ProLobby.url,
    imageAlt: "Hotel lobby with autonomous cleaning robot",
    category: "Hotels",
    date: "2026-06-05",
    readTime: "6 min read",
    author: "Neil AI Team",
    tags: ["Hotels", "Cleaning Robots", "Hospitality", "Operations"],
    intro:
      "Hospitality operators ask the same question over and over: when does a cleaning robot actually make sense for our property? Here's the honest answer.",
    sections: [
      {
        heading: "Signs you're ready",
        body: [
          "You have large lobby or hallway square footage cleaned daily. You're paying overtime for overnight cleaning. Housekeeping turnover is a recurring problem. Guests have commented on inconsistent floor care.",
        ],
      },
      {
        heading: "Signs you're not ready yet",
        body: [
          "Your property is small, your floor plan is highly broken-up, or your current crew already has plenty of capacity. In those cases, AI voice automation or front-desk tools usually give you more value first.",
        ],
      },
      {
        heading: "What to pilot first",
        body: [
          "Start with a single robot on a single shift. Measure coverage, guest feedback, and labor relief for 60 days. Expand only when the data supports it.",
        ],
      },
    ],
    related: [
      "cc1-pro-vs-traditional-cleaning",
      "pudu-cleaning-robots-reduce-burnout",
      "complete-roi-guide-ai-robotics",
    ],
  },
  {
    slug: "restaurants-service-robots-without-replacing-staff",
    title: "How Restaurants Can Use Service Robots Without Replacing Staff",
    excerpt:
      "BellaBot Pro and KettyBot Pro help servers move more plates with less effort. Here's how to position robots as a teammate, not a threat.",
    image: bellabotPro.url,
    imageAlt: "PUDU BellaBot Pro serving in a restaurant",
    category: "Restaurants",
    date: "2026-06-02",
    readTime: "7 min read",
    author: "Neil AI Team",
    tags: ["BellaBot Pro", "KettyBot Pro", "Restaurants", "Service Robots"],
    intro:
      "Restaurants that bring in service robots and frame them as 'we're firing servers' fail fast. Restaurants that frame them as 'we're giving servers their legs back' succeed.",
    sections: [
      {
        heading: "What the robot actually does",
        body: [
          "Carries multiple trays from the kitchen to a table zone. Returns dirty dishes to the dishpit. Greets and guides guests at the door (KettyBot Pro).",
          "It does not take orders, read the room, or handle a complaint. That's your team.",
        ],
      },
      {
        heading: "How to roll it out with your staff",
        body: [
          "Introduce it as a tool that removes the heavy back-and-forth so servers can focus on guests and tips. Train the team to load and dispatch it. Celebrate the wins publicly.",
        ],
      },
      {
        heading: "What changes in week one",
        body: [
          "Servers walk less, carry less, and spend more time at the table. Turnover slows. Tips usually go up because guest interaction goes up.",
        ],
      },
    ],
    pullQuote:
      "Frame the robot as a teammate, not a threat — that's the entire difference.",
    related: [
      "pudu-cleaning-robots-reduce-burnout",
      "voice-ai-business-development-rep",
      "complete-roi-guide-ai-robotics",
    ],
  },
  {
    slug: "voice-ai-business-development-rep",
    title: "Voice AI: A 24/7 Business Development Rep for Your Company",
    excerpt:
      "A practical look at how AI voice agents answer calls, qualify leads, and book appointments around the clock — without replacing your sales team.",
    image: aiDashboard.url,
    imageAlt: "AI voice and CRM workflow dashboard",
    category: "AI Automation",
    date: "2026-05-29",
    readTime: "7 min read",
    author: "Neil AI Team",
    tags: ["Voice AI", "AI Automation", "Sales", "Lead Generation"],
    intro:
      "Most small and mid-sized businesses don't lose deals because their team isn't good. They lose deals because nobody picked up at 7:42pm on a Tuesday. Voice AI solves exactly that.",
    sections: [
      {
        heading: "What a voice agent handles well",
        body: [
          "Inbound calls after hours and during peak. Initial qualification questions. Booking appointments directly into your calendar. Routing serious buyers to a real human fast.",
        ],
      },
      {
        heading: "What it doesn't try to do",
        body: [
          "Close complex deals. Negotiate. Handle nuanced complaints. Those still belong with your team — and now your team has more time for them because the noise is gone.",
        ],
      },
      {
        heading: "What you should measure",
        body: [
          "Recovered missed calls. Appointments booked outside of office hours. Speed-to-lead. Show-up rate. Those four numbers tell you whether the system is paying for itself.",
        ],
      },
    ],
    related: [
      "churches-ai-call-answering",
      "complete-roi-guide-ai-robotics",
      "is-your-business-ready-for-ai-robotics",
    ],
  },
  {
    slug: "churches-ai-call-answering",
    title: "How Churches Can Use AI to Answer Calls and Manage Follow-Up",
    excerpt:
      "Practical, faith-friendly automation for ministries: never miss a prayer request, a visitor inquiry, or a member follow-up — without overloading your staff.",
    image: aiDashboard.url,
    imageAlt: "AI follow-up workflow for ministries",
    category: "Churches",
    date: "2026-05-25",
    readTime: "6 min read",
    author: "Neil AI Team",
    tags: ["Churches", "AI Automation", "Voice AI", "Ministry"],
    intro:
      "Ministries grow on relationships. The job of AI here is to make sure no one slips through the cracks while your team focuses on people.",
    sections: [
      {
        heading: "Where AI helps a church first",
        body: [
          "Answering calls during the week when the office is short-staffed. Capturing prayer requests with care. Sending visitor follow-ups. Reminding members about events and small groups.",
        ],
      },
      {
        heading: "What stays with your team",
        body: [
          "Pastoral care, hard conversations, hospital visits, leadership development. Anything that requires presence stays with people. AI handles the operational layer behind the scenes.",
        ],
      },
      {
        heading: "Getting started simply",
        body: [
          "Most churches start with a single voice agent on the main line plus an automated follow-up flow for first-time visitors. Small, focused, and respectful of the people on the other end.",
        ],
      },
    ],
    related: [
      "voice-ai-business-development-rep",
      "is-your-business-ready-for-ai-robotics",
      "complete-roi-guide-ai-robotics",
    ],
  },
  {
    slug: "warehouse-automation-biggest-impact",
    title: "Warehouse Automation: Where Robots Make the Biggest Impact",
    excerpt:
      "The PUDU T600 and fleet-managed transport robots aren't science fiction — they're a practical answer to repetitive pallet and case movement.",
    image: puduT600.url,
    imageAlt: "PUDU T600 autonomous pallet transport robot in a warehouse",
    category: "Warehouses",
    date: "2026-05-22",
    readTime: "8 min read",
    author: "Neil AI Team",
    tags: ["Warehouses", "PUDU T600", "Logistics", "Automation"],
    intro:
      "Warehouse automation gets oversold. Here's a grounded look at where transport robots like the PUDU T600 actually move the needle — and where they don't.",
    sections: [
      {
        heading: "The right jobs for transport robots",
        body: [
          "Repetitive point-to-point pallet and case moves. Long indoor runs between staging and outbound. Overnight replenishment loops.",
          "The T600 carries meaningful payload, navigates with 360° lidar, and works in a managed fleet — perfect for these patterns.",
        ],
      },
      {
        heading: "The wrong jobs (for now)",
        body: [
          "Highly variable picking, anything requiring fine manipulation, or environments without a stable layout. Save those for humans.",
        ],
      },
      {
        heading: "What changes for your team",
        body: [
          "Less time pushing heavy carts across the building. More time on tasks that require judgment. Fewer injuries from repetitive lifting and walking.",
        ],
      },
    ],
    related: [
      "complete-roi-guide-ai-robotics",
      "pudu-cleaning-robots-reduce-burnout",
      "what-to-ask-before-buying-cleaning-robot",
    ],
  },
  {
    slug: "cooks-flea-market-sh1-case-study",
    title: "Case Study: How Cooks Flea Market Upgraded From Mop & Bucket to SH1",
    excerpt:
      "A real-world look at a high-traffic retail environment moving from manual mopping to the PUDU SH1 scrubber — what changed, what didn't, and what to expect.",
    image: cooksCase.url,
    imageAlt: "Cooks Flea Market autonomous floor cleaning deployment",
    category: "Case Studies",
    date: "2026-05-18",
    readTime: "8 min read",
    author: "Neil AI Team",
    tags: ["Case Studies", "SH1", "Cleaning Robots", "Retail"],
    intro:
      "Cooks Flea Market is exactly the kind of high-traffic, large-floor environment that punishes a manual cleaning crew. Here's how the operation changed when the SH1 was deployed.",
    sections: [
      {
        heading: "The before picture",
        body: [
          "Long aisles, heavy daily foot traffic, and a small crew running mops and buckets through the night. Coverage was inconsistent and the work was hard on people.",
        ],
      },
      {
        heading: "What deployment looked like",
        body: [
          "Site walk-through, route mapping, staff training, and a short pilot before going to full schedule. The SH1 took over the overnight scrub while the team handled detail work, restrooms, and customer-facing tasks.",
        ],
      },
      {
        heading: "What changed",
        body: [
          "Floors came up more consistent. The team stopped finishing shifts physically wrecked. Detail cleaning that used to get skipped started getting done — because there was finally time for it.",
        ],
      },
    ],
    pullQuote:
      "The robot didn't take anyone's job. It took the part of the job that was wearing them out.",
    related: [
      "pudu-cleaning-robots-reduce-burnout",
      "cc1-pro-vs-traditional-cleaning",
      "what-to-ask-before-buying-cleaning-robot",
    ],
  },
  {
    slug: "what-to-ask-before-buying-cleaning-robot",
    title: "What to Ask Before Buying a Commercial Cleaning Robot",
    excerpt:
      "The questions every business owner should ask a vendor before signing — coverage, service, training, financing, and total cost.",
    image: warehouseNav.url,
    imageAlt: "Autonomous navigation planning for cleaning and transport robots",
    category: "Cleaning Robots",
    date: "2026-05-14",
    readTime: "6 min read",
    author: "Neil AI Team",
    tags: ["Buying Guide", "Cleaning Robots", "PUDU Robotics", "ROI"],
    intro:
      "A commercial cleaning robot is a real investment. Ask these questions before you sign anything — and walk away from any vendor that can't answer them clearly.",
    sections: [
      {
        heading: "Operations questions",
        body: [
          "What square footage and floor types does this robot actually handle well? How long does it run on a charge? What does a typical schedule look like in a space like mine?",
        ],
      },
      {
        heading: "Service and support questions",
        body: [
          "What's included in support? Who do I call if something breaks? How fast can you get on-site? Is training included for my team?",
        ],
      },
      {
        heading: "Financial questions",
        body: [
          "What's the all-in monthly cost including financing? What's the warranty? What happens at the end of a financing term?",
          "If anyone gets vague on these, that's your answer.",
        ],
      },
    ],
    related: [
      "cc1-pro-vs-traditional-cleaning",
      "complete-roi-guide-ai-robotics",
      "pudu-cleaning-robots-reduce-burnout",
    ],
  },
  {
    slug: "bellabot-vs-bellabot-pro",
    title: "BellaBot vs BellaBot Pro: Commercial Delivery Robot Comparison",
    excerpt:
      "A side-by-side comparison of BellaBot and BellaBot Pro — the 18.5-inch advertising screen, battery life, payload, and which one actually fits your restaurant.",
    image: bellabotPro.url,
    imageAlt: "BellaBot Pro service robot delivering trays in a restaurant",
    category: "Service Robots",
    date: "2026-06-20",
    readTime: "8 min read",
    author: "Neil AI Team",
    tags: ["BellaBot", "BellaBot Pro", "PUDU Robotics", "Comparison", "Restaurants"],
    intro:
      "BellaBot and BellaBot Pro look similar at a glance, but the Pro is a meaningfully different machine. Here is an honest, spec-by-spec comparison so you can pick the right commercial delivery robot for your restaurant, hotel, or senior living facility.",
    sections: [
      {
        heading: "Quick verdict",
        body: [
          "If you run a busy restaurant, hotel, or multi-zone facility and want a longer shift, a larger advertising screen, and smoother navigation, BellaBot Pro is the better long-term buy.",
          "If you run a smaller venue with predictable layouts and a tighter budget, the original BellaBot still delivers strong ROI without the upgraded hardware.",
        ],
      },
      {
        heading: "Display and customer experience",
        body: [
          "BellaBot Pro features a large 18.5-inch advertising and interaction screen on the front, giving you a real digital signage surface for promotions, drink specials, loyalty programs, and brand content as the robot moves through the room.",
          "The original BellaBot has a smaller expressive face display focused on the cat-like personality. Great for delight, limited for monetization. The Pro keeps the personality and adds the advertising real estate on top.",
        ],
      },
      {
        heading: "Battery life and shift length",
        body: [
          "BellaBot Pro extends operating time per charge, which matters when you are pushing the robot through a full lunch and dinner service without swapping batteries.",
          "Original BellaBot still handles a typical shift, but Pro gives operations managers more headroom for back-to-back peaks, brunch-through-dinner runs, and 24/7 hospitality environments.",
        ],
      },
      {
        heading: "Payload, trays, and throughput",
        body: [
          "Both robots use a four-tray layout with roughly 10 kg per tray, totaling around 40 kg of usable payload — enough to clear a six-top and bring back bus tubs in a single trip.",
          "Pro tightens up navigation and acceleration so trips between the kitchen pass and the dining floor are faster and steadier, which adds up across hundreds of runs per week.",
        ],
      },
      {
        heading: "Navigation and safety",
        body: [
          "BellaBot Pro uses PUDU's latest SLAM and obstacle-avoidance stack with improved sensor fusion, giving you cleaner pathing in crowded dining rooms and tighter aisles.",
          "Original BellaBot is still safe and reliable, but Pro handles edge cases — children, dropped napkins, sudden chair movements — with more confidence out of the box.",
        ],
      },
      {
        heading: "Which one should you buy?",
        body: [
          "Buy BellaBot Pro if you want digital signage revenue from the front screen, longer battery for double shifts, and the most current navigation hardware.",
          "Buy the original BellaBot if you want proven, lower-cost service automation for a single-shift venue and you do not need the larger advertising display.",
          "Either way, ask for a written ROI breakdown that includes labor hours offset, financing, and a 60-day pilot plan before you commit.",
        ],
      },
    ],
    pullQuote:
      "The Pro is not just a face-lift — the 18.5-inch screen and extended battery turn it into a marketing channel that pays for itself.",
    related: [
      "restaurants-service-robots-without-replacing-staff",
      "complete-roi-guide-ai-robotics",
      "is-your-business-ready-for-ai-robotics",
    ],
  },
];


export const getPostBySlug = (slug: string) =>
  blogPosts.find((p) => p.slug === slug);

export const getRelatedPosts = (slugs: string[]) =>
  slugs.map((s) => blogPosts.find((p) => p.slug === s)).filter(Boolean) as BlogPost[];

export const featuredPost = blogPosts.find((p) => p.featured) ?? blogPosts[0];
